import { useListDeletion } from "@/components/settings/lists/useListDeletion";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { Link2, Plus, Trash2, X } from "lucide-react";
import AppShell from "@/components/foundations/core/AppShell/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BackgroundColorPicker,
  ColorSwatchGrid,
  ColorPreviewCard,
} from "@/components/foundations/inputs/ColorPicker/ColorPicker";
import StylizedIcon from "@/components/foundations/style/StylizedIcon/StylizedIcon";
import SortableListsManager, {
  type SortableList,
  type SortableListsEntry,
} from "@/components/foundations/lists/SortableListsManager/SortableListsManager";
import {
  DEFAULT_AREA_COLOR,
  getListColor,
  resolvePairedColorKeys,
  type ListColorKey,
} from "@/lib/listColors";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

function numericListId(id: string) {
  return Number(id.slice(id.indexOf(":") + 1));
}

export default function CategoryEditor() {
  const [, params] = useRoute<{ id: string }>("/settings/categories/:id");
  const [, setLocation] = useLocation();
  const categoryId = params ? Number(params.id) : 0;
  const utils = trpc.useUtils();
  const categoryQuery = trpc.categories.get.useQuery(
    { id: categoryId },
    { enabled: categoryId > 0 }
  );
  const listsQuery = trpc.lists.list.useQuery();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [primary, setPrimary] = useState<ListColorKey>(DEFAULT_AREA_COLOR);
  const [secondary, setSecondary] = useState<ListColorKey | null>(null);
  const [layout, setLayout] = useState<SortableListsEntry[]>([]);
  const [newListOpen, setNewListOpen] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [addListOpen, setAddListOpen] = useState(false);
  const [selectedListId, setSelectedListId] = useState("");
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());

  const categoryLists = useMemo(
    () =>
      (listsQuery.data ?? [])
        .filter(list => list.categoryId === categoryId)
        .sort((left, right) => left.priorityNumber - right.priorityNumber),
    [listsQuery.data, categoryId]
  );

  useEffect(() => {
    const category = categoryQuery.data;
    if (!category) return;
    setName(category.name);
    setDescription(category.description ?? "");
    setPrimary((category.color as ListColorKey) ?? DEFAULT_AREA_COLOR);
    setSecondary((category.bgColor as ListColorKey | null) ?? null);
  }, [categoryQuery.data]);

  useEffect(() => {
    setLayout(
      categoryLists.map(list => {
        const resolved = resolvePairedColorKeys({
          primaryColor: list.color,
          secondaryColor: list.bgColor,
        });
        const primaryColor = getListColor(resolved.primary);
        const secondaryColor = getListColor(resolved.secondary);
        return {
          id: `list:${list.id}`,
          type: "list" as const,
          label: list.name,
          categoryId: null,
          active: list.isGlobalActive,
          taskCount: list.taskCount,
          patternCount: list.patternCount,
          sortIndicatorColors: {
            background: secondaryColor.soft,
            foreground: primaryColor.ink,
          },
        };
      })
    );
  }, [categoryLists]);

  const updateCategory = trpc.categories.update.useMutation({
    onSuccess: () => {
      void Promise.all([
        utils.categories.get.invalidate({ id: categoryId }),
        utils.categories.list.invalidate(),
        utils.lists.list.invalidate(),
      ]);
      toast.success("Category updated");
    },
  });
  const reorderLists = trpc.categories.reorderLists.useMutation();
  const updateList = trpc.lists.update.useMutation({
    onSuccess: () => utils.lists.list.invalidate(),
  });
  const deletion = useListDeletion();
  const addList = trpc.categories.addList.useMutation();
  const createList = trpc.lists.create.useMutation();

  const availableLists = (listsQuery.data ?? []).filter(
    list => list.kind !== "inbox" && list.categoryId !== categoryId
  );

  async function refreshLists() {
    await Promise.all([
      utils.lists.list.invalidate(),
      utils.categories.list.invalidate(),
    ]);
  }

  async function handleCreateList() {
    const nextName = newListName.trim();
    if (!nextName) return;
    try {
      await createList.mutateAsync({
        name: nextName,
        categoryId,
        color: null,
        bgColor: null,
      });
      await refreshLists();
      setNewListName("");
      setNewListOpen(false);
      toast.success("List created");
    } catch {
      toast.error("Could not create list");
    }
  }

  async function handleAddList() {
    const listId = Number(selectedListId);
    if (!listId) return;
    try {
      await addList.mutateAsync({ categoryId, listId });
      await refreshLists();
      setSelectedListId("");
      setAddListOpen(false);
      toast.success("List added to category");
    } catch {
      toast.error("Could not add list");
    }
  }

  if (!categoryId) return null;
  if (categoryQuery.isLoading) {
    return (
      <AppShell backTo="/settings/lists" title="Category">
        <div className="container max-w-3xl space-y-4 py-8">
          <div className="h-10 w-40 animate-pulse rounded bg-muted/40" />
          <div className="h-80 animate-pulse rounded-xl bg-muted/40" />
        </div>
      </AppShell>
    );
  }
  if (!categoryQuery.data) {
    return (
      <AppShell backTo="/settings/lists" title="Category">
        <div className="container max-w-3xl py-12 text-center">
          <h1 className="font-display text-2xl">Category not found</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            It may have been deleted or moved.
          </p>
        </div>
      </AppShell>
    );
  }

  const resolvedColors = resolvePairedColorKeys({
    primaryColor: primary,
    secondaryColor: secondary,
  });
  const primaryColor = getListColor(resolvedColors.primary);
  const secondaryColor = getListColor(resolvedColors.secondary);

  function handleLayoutChange(next: SortableListsEntry[]) {
    const previousById = new Map(
      layout
        .filter((entry): entry is SortableList => entry.type === "list")
        .map(entry => [entry.id, entry])
    );
    setLayout(next);
    const lists = next.filter(
      (entry): entry is SortableList => entry.type === "list"
    );
    for (const list of lists) {
      const previous = previousById.get(list.id);
      if (previous && previous.active !== list.active) {
        updateList.mutate({
          id: numericListId(list.id),
          isGlobalActive: list.active,
        });
      }
    }
    const orderedIds = lists.map(list => numericListId(list.id));
    saveQueueRef.current = saveQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        await reorderLists.mutateAsync({ categoryId, orderedIds });
      })
      .catch(() => {
        toast.error("Could not save list order");
        void utils.lists.list.invalidate();
      });
  }

  return (
    <AppShell
      backTo="/settings/lists"
      title={categoryQuery.data?.name ?? "Category"}
    >
      <div className="container max-w-3xl space-y-10 py-8">
        <section className="space-y-5">
          <div className="flex items-center gap-3">
            <StylizedIcon
              primaryColor={primaryColor.base}
              secondaryColor={secondaryColor.soft}
            />
            <h1 className="font-display text-3xl tracking-tight">Category</h1>
            <Button
              className="ui-delete-button ml-auto"
              variant="destructive"
              disabled={deletion.isPending}
              onClick={() =>
                void deletion.remove(
                  "category",
                  categoryId,
                  categoryQuery.data!.name,
                  () => setLocation("/settings/lists")
                )
              }
            >
              <Trash2 className="h-4 w-4" /> Delete category
            </Button>
          </div>

          <div className="space-y-4 rounded-xl border border-border/60 bg-card p-5">
            <div className="space-y-2">
              <Label htmlFor="category-editor-name">Name</Label>
              <Input
                id="category-editor-name"
                value={name}
                onChange={event => setName(event.target.value)}
                onBlur={() => {
                  const next = name.trim();
                  if (next && next !== categoryQuery.data?.name) {
                    updateCategory.mutate({ id: categoryId, name: next });
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="category-editor-description">Description</Label>
                {description && (
                  <button
                    type="button"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    aria-label="Clear description"
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => {
                      setDescription("");
                      updateCategory.mutate({
                        id: categoryId,
                        description: null,
                      });
                    }}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <Textarea
                id="category-editor-description"
                rows={2}
                value={description}
                onChange={event => setDescription(event.target.value)}
                onBlur={() => {
                  if (description !== (categoryQuery.data?.description ?? "")) {
                    updateCategory.mutate({
                      id: categoryId,
                      description: description.trim() || null,
                    });
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Primary color
              </p>
              <ColorSwatchGrid
                value={primary}
                variant="bar"
                onChange={color => {
                  if (!color) return;
                  setPrimary(color);
                  updateCategory.mutate({ id: categoryId, color });
                }}
              />
            </div>
            <BackgroundColorPicker
              value={secondary}
              label="Secondary color"
              showAuto
              autoColor={primary}
              onChange={color => {
                setSecondary(color);
                updateCategory.mutate({ id: categoryId, bgColor: color });
              }}
            />
            <div className="space-y-1.5 pt-1">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Preview
              </p>
              <ColorPreviewCard
                barColor={primary}
                bgColor={secondary}
                title="Sample task"
                label={name.trim() || "Category"}
              />
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-2xl tracking-tight">Lists</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Sort the lists within this category. Their position remains
                connected to the main list order.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Dialog open={addListOpen} onOpenChange={setAddListOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="gap-1.5">
                    <Link2 className="h-4 w-4" />
                    Add list
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add an existing list</DialogTitle>
                    <DialogDescription>
                      Moving a list here removes it from its current category.
                    </DialogDescription>
                  </DialogHeader>
                  {availableLists.length === 0 ? (
                    <p className="py-4 text-sm text-muted-foreground">
                      Every custom list is already in this category.
                    </p>
                  ) : (
                    <div className="space-y-2 py-2">
                      <Label htmlFor="existing-list">List</Label>
                      <Select
                        value={selectedListId}
                        onValueChange={setSelectedListId}
                      >
                        <SelectTrigger id="existing-list">
                          <SelectValue placeholder="Choose a list" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableLists.map(list => (
                            <SelectItem key={list.id} value={String(list.id)}>
                              {list.name}
                              {list.categoryName
                                ? ` (${list.categoryName})`
                                : " (No category)"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <DialogFooter>
                    <Button
                      variant="ghost"
                      onClick={() => setAddListOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      disabled={!selectedListId || addList.isPending}
                      onClick={handleAddList}
                    >
                      Add list
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Dialog open={newListOpen} onOpenChange={setNewListOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="gap-1.5">
                    <Plus className="h-4 w-4" />
                    New list
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>New list</DialogTitle>
                    <DialogDescription>
                      The new list will inherit this category&apos;s colors.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-2 py-2">
                    <Label htmlFor="new-category-list-name">Name</Label>
                    <Input
                      id="new-category-list-name"
                      autoFocus
                      value={newListName}
                      placeholder="e.g. Meetings"
                      onChange={event => setNewListName(event.target.value)}
                      onKeyDown={event => {
                        if (event.key === "Enter") void handleCreateList();
                      }}
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      variant="ghost"
                      onClick={() => setNewListOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      disabled={!newListName.trim() || createList.isPending}
                      onClick={handleCreateList}
                    >
                      Create list
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {listsQuery.isLoading ? (
            <div className="space-y-2">
              {[0, 1].map(index => (
                <div
                  key={index}
                  className="h-16 animate-pulse rounded-xl bg-muted/40"
                />
              ))}
            </div>
          ) : layout.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              No lists are assigned to this category.
            </div>
          ) : (
            <SortableListsManager
              initialLayout={layout}
              layout={layout}
              onLayoutChange={handleLayoutChange}
              onOpenList={list =>
                setLocation(`/settings/lists/${numericListId(list.id)}`)
              }
              onDeleteList={list => {
                void deletion.remove(
                  "list",
                  numericListId(list.id),
                  list.label
                );
              }}
            />
          )}
        </section>
      </div>
    </AppShell>
  );
}
